import ResizeObserver from 'resize-observer-polyfill';
import { Edge, Side } from '@sierralabs/workflow';

import { Entity, EntityManager } from '.';

export class Connector {
  protected edges: Edge[] = [];
  private observer?: ResizeObserver;
  private observerCallback?: () => void;
  private prevFromEl: HTMLElement | null = null;
  private prevToEl: HTMLElement | null = null;
  private prevSourceEdge?: Edge;
  private prevTargetEdge?: Edge;

  constructor(
    private readonly manager: EntityManager,
    public source: Entity,
    public target: Entity | null
  ) {}

  get lastEdge(): Edge {
    return (
      this.edges[this.edges.length - 1] || { x: 0, y: 0, side: Side.Unknown }
    );
  }

  // skip calculation if values compared to previous edges are the same
  private doCalculateEdges(): boolean {
    const { source, target, prevSourceEdge, prevTargetEdge } = this;
    if (!source || !target) {
      return false;
    }
    if (!prevSourceEdge || !prevTargetEdge) {
      return true;
    }

    const sourceEdge: Edge = {
      x: source.rect.right,
      y: source.rect.center.y,
      side: Side.Unknown
    };
    const targetEdge: Edge = {
      x: target.rect.left,
      y: target.rect.center.y,
      side: Side.Unknown
    };
    return !(
      prevSourceEdge.x === sourceEdge.x &&
      prevSourceEdge.y === sourceEdge.y &&
      prevTargetEdge.x === targetEdge.x &&
      prevTargetEdge.y === targetEdge.y
    );
  }

  calculateEdges() {
    const { source, target } = this;
    if (!source || !target || !this.doCalculateEdges()) {
      return this.edges;
    }

    // TODO: apply smooth edges
    let sourceEdge: Edge = {
      x: source.rect.right,
      y: source.rect.center.y,
      side: Side.Right
    };
    let targetEdge: Edge = {
      x: target.rect.left,
      y: target.rect.center.y,
      side: Side.Left
    };

    // sourceEdge = this.distributeEdges(sourceEdge, this.source);
    // targetEdge = this.distributeEdges(targetEdge, this.target);

    this.prevSourceEdge = sourceEdge;
    this.prevTargetEdge = targetEdge;

    return this.generateEdgesBetweenSourceTarget(sourceEdge, targetEdge);
  }

  setObserverCallback(callback: () => void) {
    this.observerCallback = callback;
  }

  runObserverCallback() {
    this.observerCallback?.();
  }

  startObserver() {
    if (!this.observer) {
      this.observer = new ResizeObserver(() => {
        this.updateObservedElements();
        this.runObserverCallback();
        this.manager.runDebouncedObserverCallbacks(this);
      });
    }

    this.updateObservedElements();
  }

  updateObservedElements(observer: ResizeObserver | undefined = this.observer) {
    // check if previous elements are updated
    // in the case where elements are re-rendered
    const fromEl: HTMLElement | null = this.source.el;
    const toEl: HTMLElement | null = this.target?.el || null;

    this.prevFromEl = this.handleObserve(this.prevFromEl, fromEl, observer);
    this.prevToEl = this.handleObserve(this.prevToEl, toEl, observer);
  }

  private handleObserve(
    prevEl: HTMLElement | null,
    newEl: HTMLElement | null,
    observer: ResizeObserver | undefined = this.observer
  ): HTMLElement | null {
    if (prevEl !== newEl && newEl) {
      // unobserve previous element if it changed
      if (prevEl) {
        observer?.unobserve(prevEl);
      }
      observer?.observe(newEl);
      return newEl;
    }
    return prevEl;
  }

  // implementation from workflow connector

  private generateEdgesBetweenSourceTarget(sourceEdge: Edge, targetEdge: Edge) {
    this.edges = [sourceEdge];
    if (sourceEdge.x !== targetEdge.x || sourceEdge.y !== targetEdge.y) {
      if (
        (sourceEdge.side === Side.Top || sourceEdge.side === Side.Bottom) &&
        (targetEdge.side === Side.Bottom || targetEdge.side === Side.Top)
      ) {
        const mid = sourceEdge.y + (targetEdge.y - sourceEdge.y) / 2;
        this.v(mid).h(targetEdge.x).v(targetEdge.y, targetEdge.side);
      } else if (
        (sourceEdge.side === Side.Top || sourceEdge.side === Side.Bottom) &&
        (targetEdge.side === Side.Left || targetEdge.side === Side.Right)
      ) {
        this.v(targetEdge.y).h(targetEdge.x, targetEdge.side);
      } else if (
        (sourceEdge.side === Side.Left || sourceEdge.side === Side.Right) &&
        (targetEdge.side === Side.Top || targetEdge.side === Side.Bottom)
      ) {
        this.h(targetEdge.x).v(targetEdge.y, targetEdge.side);
      } else if (
        (sourceEdge.side === Side.Left || sourceEdge.side === Side.Right) &&
        (targetEdge.side === Side.Left || targetEdge.side === Side.Right)
      ) {
        const mid = sourceEdge.x + (targetEdge.x - sourceEdge.x) / 2;
        this.h(mid).v(targetEdge.y).h(targetEdge.x, targetEdge.side);
      }
    }
    // this.edges.push(targetEdge);
    return this.edges;
  }

  private distributeEdges(edge: Edge, entity: Entity | null): Edge {
    if (!entity || edge.side === Side.Unknown) {
      return edge;
    }
    // get existing connectors pointing to and from entity of same side
    const connectors: Connector[] = this.manager
      .getConnectors()
      .filter((connector: Connector) => {
        const connectorSources: boolean =
          connector.source === entity && connector.edges[0]?.side === edge.side;
        const connectorTargets: boolean =
          connector.target === entity && connector.lastEdge.side === edge.side;
        return (
          connector.edges.length > 0 && (connectorSources || connectorTargets)
        );
      });

    // sort the connectors by position relative to entity side
    const index: number = connectors
      .sort((c1: Connector, c2: Connector) => {
        switch (edge.side) {
          case Side.Top:
          case Side.Bottom:
            return (
              c1.source.rect.x +
              (c1.source.rect.width || 0) / 2 -
              (c2.source.rect.x + (c2.source.rect.width || 0) / 2)
            );
          case Side.Left:
          case Side.Right:
            return (
              c1.source.rect.y +
              (c1.source.rect.height || 0) / 2 -
              (c2.source.rect.y + (c2.source.rect.height || 0) / 2)
            );
          default:
            return 0;
        }
      })
      .indexOf(this);

    if (index < 0) {
      return edge;
    }
    edge = { ...edge };
    // update the edge with correct distribution
    const { rect } = entity;
    switch (edge.side) {
      case Side.Top:
      case Side.Bottom:
        edge.x = rect.x + (index + 1) * (rect.width / (connectors.length + 1));
        break;
      case Side.Left:
      case Side.Right:
        edge.y = rect.y + (index + 1) * (rect.height / (connectors.length + 1));
        break;
    }

    return edge;
  }

  /**
   * Implement vertical movement from last edge similar to SVG path logic
   * @param y
   * @param side
   */
  v(y: number, side: Side = Side.Unknown) {
    this.edges.push({ ...this.lastEdge, y, side });
    return this;
  }

  /**
   * Implement horizontal movement from last edge similar to SVG path logic
   * @param x
   * @param side
   */
  h(x: number, side: Side = Side.Unknown) {
    this.edges.push({ ...this.lastEdge, x, side });
    return this;
  }
}
